import React, { useRef, useState } from 'react'
import { FlexByRow, XDivider, HalfScreen } from 'irmlib/styles'
import { closeGeneralDialog, openInformationDialog, setBusy, clearBusy, closeInformationDialog } from 'irmlib/containers'
import styled from 'styled-components'
import { clearReduxGlobal, setUser } from '../../redux/global'
import {
  beginWorkerPinLogin,
  cancelWorkerPinLogin,
  getWorkerInfo,
  gdeWorkerRole,
  recordLogin,
  submitWorkerPin,
} from '../../services/firestore/login'
import { auth, GoogleAuthProvider, EmailAuthProvider } from '../../utils/firestore'
import { userRoleAdmin, userRoleUser, userRoleSupervisor, userRoleSuperUser } from '../../common/constants'

import StyledFirebaseAuth from 'react-firebaseui/StyledFirebaseAuth'
import { Label, OutlinedButton, ContainedButton } from 'irmlib/components/Controls'
import { TextField } from 'irmlib/components/FormControls'
import doLogin from '.'
import { loadApiCaches } from '../../services/firestore/cache'
import { useFormPageHandler } from 'irmlib/hooks'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { t } from 'i18next'

export const registerAuthListener = async () => {
  auth.onAuthStateChanged(async (user) => {
    if (user) {
      const email = user.email
      if (user.isAnonymous !== true) {
        await getWorkerAndRolesVerification(email, user)
      }
    }
  })
}

const getWorkerAndRolesVerification = async (email, user) => {
  try {
    setBusy()

    const worker = await getWorkerInfo(email)
    if (worker.size === 0) {
      throw new Error(t('login.accessDeniedError'))
    }
    const user = worker.docs[0].data()
    const workerUserRoles = await gdeWorkerRole(user.workerId)
    const workerDistrict = workerUserRoles.data().district
    // Converting Array to string
    const districtString = `${workerDistrict}`
    const userRole = workerUserRoles.data()
    if (!userRole) {
      throw new Error(t('login.accessDeniedError'))
    }

    if (!user.isActiveWorker) {
      throw new Error(t('login.accessDeniedError'))
    }

    if (!userRole.isActive) {
      throw new Error(t('login.userNotAllowed'))
    }

    user.name = `${user.firstName} ${user.lastName}`
    // delete Object.assign(user, { district: user.gdeDistrict }).gdeDistrict
    delete Object.assign(user, { district: districtString })
    delete userRole.gdePin
    user.userRolesObj = userRole

    if (userRole.gdeRole.toUpperCase() === userRoleAdmin) {
      user.role = userRoleAdmin
    } else if (userRole.gdeRole.toUpperCase() === userRoleSuperUser) {
      user.role = userRoleSuperUser
    } else if (userRole.gdeRole.toUpperCase() === userRoleSupervisor) {
      user.role = userRoleSupervisor
    } else {
      user.role = userRoleUser
    }

    setUser(user)
    clearBusy()
    closeGeneralDialog()
    loadApiCaches()
    recordLogin(user)
    return true
  } catch (error) {
    clearBusy()
    openInformationDialog({ message: `${error}`, onOk: () => firebaseSignOutAndDeleteAndReload(user) })
    return false
  }
}

const firebaseSignOutAndDeleteAndReload = (user) => {
  auth.signOut().then(() => user.delete())
  closeGeneralDialog()
  doLogin()
}

const LoginButtons = () => {
  const uiConfig = {
    signInFlow: 'popup',
    signInOptions: [
      GoogleAuthProvider.PROVIDER_ID,
      EmailAuthProvider.PROVIDER_ID
    ],
    callbacks: {
      signInSuccessWithAuthResult: () => false,
    },
  }
  return (
    <StyledFirebaseAuth uiConfig={uiConfig} firebaseAuth={auth} />
  )
}

const LoginBodyContainer = styled.div`
  width: 550px;
  min-height: 180px;
`
const Container = styled(FlexByRow)`
  justify-content: center;
  align-items: center;
`
const VerticalDivider = () =>
  <XDivider height='5px' />

const PartitionDivider = () =>
  <XDivider width='40px' />

const CenterText = styled(Label)`
  text-align: center;
`
const SELECT_WORKER_ID = 0
const SELECT_PIN = 1
const SELECT_CONFIRM_PIN = 2
const ENTER_PIN = 3
const VERIFY_PIN = 'verifyPin'
const SET_PIN = 'setPin'

const getLoginRole = (gdeRole) => {
  const normalizedRole = gdeRole.toUpperCase()
  if (normalizedRole === userRoleAdmin) return userRoleAdmin
  if (normalizedRole === userRoleSuperUser) return userRoleSuperUser
  if (normalizedRole === userRoleSupervisor) return userRoleSupervisor
  return userRoleUser
}

const buildWorkerLoginUser = ({ worker, gdeRole }) => {
  // Defense in depth — backend already enforces eligibility; mirror Google/email checks.
  if (!worker?.isActiveWorker || worker.isDeleted === true) {
    throw Object.assign(new Error('Access denied'), {
      code: 'functions/permission-denied',
    })
  }
  if (!gdeRole?.isActive) {
    throw new Error('User not allowed')
  }
  if (!gdeRole.gdeRole) {
    throw new Error('Invalid worker login profile response')
  }
  // Workers without a GDE district are allowed to sign in, matching the previous flow.
  const districts = gdeRole.district
  const district = Array.isArray(districts) && districts.length > 0 ? districts[0] : ''
  return {
    ...worker,
    name: `${worker.firstName} ${worker.lastName}`,
    district,
    userRolesObj: gdeRole,
    role: getLoginRole(gdeRole.gdeRole),
  }
}

const isIncorrectPinError = (error) => {
  const message = `${error?.message || ''}`.toLowerCase()
  return error?.code === 'functions/permission-denied' &&
    message.includes('incorrect pin')
}

const getWorkerLoginErrorMessage = (error, translate) => {
  const message = `${error?.message || ''}`.toLowerCase()
  if (isIncorrectPinError(error)) {
    return translate('messages.incorrectPin')
  }
  if (message.includes('user not allowed')) {
    return translate('login.userNotAllowed')
  }
  if (
    error?.code === 'functions/permission-denied' ||
    message.includes('access denied')
  ) {
    return translate('login.accessDeniedError')
  }
  return translate('login.someThingWentWrongError')
}

const LoginBody = () => {
  const { t } = useTranslation()
  const refLocalBusyCursor = useRef(null)

  const getService = async () => {
    const getObject = {
    }
    return Promise.resolve(getObject)
  }

  const formTemplate = [
    {
      label: 'login.workerID',
      fieldKey: 'workerID',
    },
    {
      label: 'login.pin',
      fieldKey: 'pin',
    },
    {
      label: 'login.confirmPin',
      fieldKey: 'confirmPin',
    },
    {
      label: 'login.enterPin',
      fieldKey: 'enterPin',
    }
  ]

  const [arrRefs, data] = useFormPageHandler({
    formTemplate,
    service: getService,
    busyOverride: refLocalBusyCursor,
  })

  const [loginButtonsKey, setLoginButtonsKey] = useState(0)
  const remountLoginButtons = () => setLoginButtonsKey((key) => key + 1)

  const cancelPinLogin = async () => {
    await cancelWorkerPinLogin()
    clearReduxGlobal()
    remountLoginButtons()
    closeInformationDialog()
  }

  const handleWorkerLoginError = async (error) => {
    clearBusy()
    await cancelWorkerPinLogin()
    clearReduxGlobal()
    remountLoginButtons()
    closeInformationDialog()
    openInformationDialog({
      message: getWorkerLoginErrorMessage(error, t),
      onOk: () => closeInformationDialog(),
    })
  }

  const completeWorkerLogin = async (profile) => {
    const user = buildWorkerLoginUser(profile)
    await setUser(user)
    clearBusy()
    closeInformationDialog()
    closeGeneralDialog()
    await loadApiCaches()
    await recordLogin(user)
  }

  const validatePin = (ref) => {
    const pin = ref.current.getValue()
    if (!/^\d{3}$/.test(pin)) {
      ref.current.setErrorMsg(pin?.length === 3 ? 'messages.mustBeinNumbers' : 'login.pinThreeDigit')
      return false
    }
    return true
  }

  const onPasswordChanged = (e) => {
    const value = e?.target?.value
    if (e.keyCode === 13 && value !== '' && validatePin(arrRefs[SELECT_PIN])) {
      arrRefs[SELECT_CONFIRM_PIN].current.focus()
    }
  }

  const setNewPin = async () => {
    if (!validatePin(arrRefs[SELECT_PIN])) return
    const pin = arrRefs[SELECT_PIN].current.getValue()
    if (pin !== arrRefs[SELECT_CONFIRM_PIN].current.getValue()) {
      arrRefs[SELECT_CONFIRM_PIN].current.setErrorMsg('login.pinMatch')
      return
    }
    try {
      setBusy()
      const workerId = arrRefs[SELECT_WORKER_ID].current.getValue()
      const profile = await submitWorkerPin(SET_PIN, workerId, pin)
      await completeWorkerLogin(profile)
    } catch (error) {
      await handleWorkerLoginError(error)
    }
  }

  const onConfirmPasswordChanged = async (e) => {
    const value = e?.target?.value
    if (e.keyCode === 13 && value !== '') {
      if (arrRefs[SELECT_PIN].current.getValue() !== arrRefs[SELECT_CONFIRM_PIN].current.getValue()) {
        arrRefs[SELECT_CONFIRM_PIN].current.setErrorMsg('login.pinMatch')
      } else {
        await setNewPin()
      }
    }
  }

  const validateEnteredPin = (e) => {
    if (e?.target?.value !== '') {
      validatePin(arrRefs[ENTER_PIN])
    }
  }

  const verifyPin = async () => {
    if (!validatePin(arrRefs[ENTER_PIN])) return
    try {
      setBusy()
      const workerId = arrRefs[SELECT_WORKER_ID].current.getValue()
      const profile = await submitWorkerPin(VERIFY_PIN, workerId, arrRefs[ENTER_PIN].current.getValue())
      await completeWorkerLogin(profile)
    } catch (error) {
      if (isIncorrectPinError(error)) {
        clearBusy()
        arrRefs[ENTER_PIN].current.setErrorMsg('messages.incorrectPin')
        return
      }
      await handleWorkerLoginError(error)
    }
  }

  const PinDialogButtons = ({ onContinue }) =>
    <>
      <OutlinedButton onClick={cancelPinLogin}>buttons.cancel</OutlinedButton>
      <ContainedButton onClick={onContinue}>buttons.Continue</ContainedButton>
    </>

  PinDialogButtons.propTypes = {
    onContinue: PropTypes.func.isRequired,
  }

  const openSetPinDialog = () => {
    openInformationDialog({
      title: t('messages.threeDigitPin'),
      noTranslateTitle: true,
      message: <>
        <div>
          <TextField
            style={{ width: 250 }}
            data={data}
            fieldTemplate={formTemplate[SELECT_PIN]}
            ref={arrRefs[SELECT_PIN]}
            inputProps={{ maxLength: 3 }}
            type="password"
            onKeyDown={onPasswordChanged}
            onBlur={() => validatePin(arrRefs[SELECT_PIN])} />
          <VerticalDivider />
        </div>
        <div>
          <TextField
            style={{ width: 250 }}
            data={data}
            fieldTemplate={formTemplate[SELECT_CONFIRM_PIN]}
            ref={arrRefs[SELECT_CONFIRM_PIN]}
            inputProps={{ maxLength: 3 }}
            type="password"
            onKeyDown={onConfirmPasswordChanged}
            onBlur={() => {
              if (arrRefs[SELECT_PIN].current.getValue() !== arrRefs[SELECT_CONFIRM_PIN].current.getValue()) {
                arrRefs[SELECT_CONFIRM_PIN].current.setErrorMsg('login.pinMatch')
              }
            }} />
          <VerticalDivider />
        </div>
      </>,
      translateMessage: true,
      buttons: <PinDialogButtons onContinue={setNewPin} />,
      closeX: true,
      onCancel: cancelPinLogin,
    })
  }

  const openEnterPinDialog = () => {
    openInformationDialog({
      title: t('messages.enterThePin'),
      message: <>
        <div>
          <TextField
            autoFocus
            style={{ width: 250 }}
            data={data}
            fieldTemplate={formTemplate[ENTER_PIN]}
            ref={arrRefs[ENTER_PIN]}
            inputProps={{ maxLength: 3 }}
            type="password"
            onKeyDown={(e) => e.keyCode === 13 ? verifyPin() : validateEnteredPin(e)}
            onBlur={validateEnteredPin} />
          <VerticalDivider />
        </div>
      </>,
      noTranslateTitle: true,
      translateMessage: true,
      buttons: <PinDialogButtons onContinue={verifyPin} />,
      closeX: true,
      onCancel: cancelPinLogin,
    })
  }

  const onWorkerIDChanged = async (e) => {
    const workerId = e?.target?.value
    if (e.keyCode !== 13 || !workerId) return
    try {
      setBusy()
      const lookup = await beginWorkerPinLogin(workerId)
      if (!lookup.pinActive) {
        throw Object.assign(new Error('Access denied'), {
          code: 'functions/permission-denied',
        })
      }
      if (lookup.pinExists) {
        openEnterPinDialog()
      } else {
        openSetPinDialog()
      }
      clearBusy()
    } catch (error) {
      await handleWorkerLoginError(error)
    }
  }

  return (
    <LoginBodyContainer id='LoginBody'>
      <CenterText>login.loginSubTitle</CenterText>
      <Container>
        <HalfScreen>
          <VerticalDivider />
          <VerticalDivider />
          <TextField
            style={{ width: 250 }}
            data={data}
            fieldTemplate={formTemplate[SELECT_WORKER_ID]}
            ref={arrRefs[SELECT_WORKER_ID]}
            onKeyDown={(e) => onWorkerIDChanged(e)} />
          <VerticalDivider />
        </HalfScreen>
        <PartitionDivider />
        <HalfScreen>
          <VerticalDivider />
          <LoginButtons key={loginButtonsKey} />
        </HalfScreen>
      </Container>
    </LoginBodyContainer>
  )
}

export const Buttons = () => {
  return null
}

export default LoginBody
